import { Controller } from "@hotwired/stimulus"

// Phase 3.8 Tier 5 Step 70 — Box Content Picker UI Element folder picker.
//
// The Stimulus controller lazy-loads Box's `picker.js` + matching
// `picker.css` from the Box CDN, mounts a `Box.ContentPicker` instance
// in folder-only mode (`type: "folder"`) authorized with the connection's
// downscoped picker token, and writes the picked folder's id + name +
// web URL into hidden form fields on selection.
//
// ── Token down-scoping ────────────────────────────────────────────────
//
// The token in `tokenValue` is a `base_explorer`-only sub-token minted
// server-side via Box's token-exchange grant (see `Box::Adapter#mint_picker_token`).
// The picker iframe gets folder-navigation rights only — it can't read
// file content, can't write, can't delete. If the exchange failed
// server-side the partial falls back to the full user token; this
// controller doesn't need to distinguish.
//
// ── Picker version ────────────────────────────────────────────────────
//
// We pin Box UI Elements to v22.0.0 (matching the script_url + stylesheet_url
// in `picker_descriptor`). Box ships breaking changes between major
// versions; the version pin keeps the picker stable across Box CDN
// rollouts. Bump when Box's breaking-changes log adds something we want.
export default class extends Controller {
  static values = {
    scriptUrl:     String,
    stylesheetUrl: String,
    token:         String,
    rootFolderId:  String,
    mountId:       String,
  }

  static targets = ["display", "folderIdField", "folderNameField", "folderUrlField",
                    "modal", "mount"]

  open(event) {
    event.preventDefault()
    if (!this.tokenValue) {
      this._setStatus("Reconnect Box to pick a folder — the OAuth token is missing.")
      return
    }
    this._loadBoxAssets()
        .then(() => this._mountPicker())
        .catch((err) => this._setStatus(`Couldn't load Box Picker: ${err.message || err}`))
  }

  close(event) {
    if (event) event.preventDefault()
    if (this.hasModalTarget) this.modalTarget.classList.add("hidden")
    this._unmountPicker()
  }

  _loadBoxAssets() {
    // The picker script attaches to `window.Box.ContentPicker`. Skip
    // the fetch if both the script and the stylesheet are already in
    // the DOM (this controller may be re-instantiated when the user
    // re-opens the modal).
    if (window.Box && window.Box.ContentPicker) {
      return this._ensureStylesheet().then(() => undefined)
    }
    return Promise.all([
      this._ensureScript(),
      this._ensureStylesheet(),
    ]).then(() => undefined)
  }

  _ensureScript() {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${this.scriptUrlValue}"]`)
      if (existing) {
        if (window.Box && window.Box.ContentPicker) {
          resolve()
        } else {
          existing.addEventListener("load", () => resolve(), { once: true })
          existing.addEventListener("error", () => reject(new Error("Box picker script load failed")))
        }
        return
      }
      const script = document.createElement("script")
      script.src = this.scriptUrlValue
      script.async = true
      script.defer = true
      script.addEventListener("load", () => resolve(), { once: true })
      script.addEventListener("error", () => reject(new Error("Box picker script load failed")))
      document.head.appendChild(script)
    })
  }

  _ensureStylesheet() {
    return new Promise((resolve, reject) => {
      if (!this.stylesheetUrlValue) {
        resolve()
        return
      }
      const existing = document.querySelector(`link[href="${this.stylesheetUrlValue}"]`)
      if (existing) {
        resolve()
        return
      }
      const link = document.createElement("link")
      link.rel = "stylesheet"
      link.href = this.stylesheetUrlValue
      link.addEventListener("load", () => resolve(), { once: true })
      link.addEventListener("error", () => reject(new Error("Box picker stylesheet load failed")))
      document.head.appendChild(link)
    })
  }

  _mountPicker() {
    if (this.hasModalTarget) this.modalTarget.classList.remove("hidden")
    if (!this.hasMountTarget) {
      this._setStatus("Internal error: picker mount node not found.")
      return
    }

    // Reset the mount node so a re-open doesn't double-mount.
    this._unmountPicker()
    this.mountTarget.innerHTML = ""

    const ContentPicker = window.Box && window.Box.ContentPicker
    if (!ContentPicker) {
      this._setStatus("Box picker library failed to initialize.")
      return
    }

    this._picker = new ContentPicker()
    this._picker.addListener("choose", (items) => this._handlePick(items))
    this._picker.addListener("cancel", () => this.close())

    this._picker.show(
      this.rootFolderIdValue || "0",
      this.tokenValue,
      {
        container: this.mountTarget,
        type: "folder",
        chooseButtonLabel: "Use this folder",
        canCreateNewFolder: true,
        // Box's picker-element supports a `defaultView` option to
        // pre-open at "All Files" rather than "Recents" — folder-only
        // mode wants the file tree, not recent-files history.
        defaultView: "files",
      }
    )
  }

  _unmountPicker() {
    if (this._picker && typeof this._picker.hide === "function") {
      try { this._picker.hide() } catch (err) { /* swallow */ }
    }
    this._picker = null
  }

  _handlePick(items) {
    const folder = (items || [])[0]
    if (!folder) return

    if (this.hasFolderIdFieldTarget)   this.folderIdFieldTarget.value   = folder.id || ""
    if (this.hasFolderNameFieldTarget) this.folderNameFieldTarget.value = folder.name || ""
    // Box's picker-element returns a `shared_link` object only when the
    // file/folder has been explicitly shared. For unshared folders, we
    // synthesize a deep link — the canonical Box folder URL is
    // `https://app.box.com/folder/<id>`.
    const webUrl = (folder.shared_link && folder.shared_link.url) ||
                   `https://app.box.com/folder/${folder.id}`
    if (this.hasFolderUrlFieldTarget)  this.folderUrlFieldTarget.value  = webUrl
    if (this.hasDisplayTarget)         this.displayTarget.value         = folder.name || ""

    this.close()
  }

  _setStatus(message) {
    if (this.hasDisplayTarget) {
      this.displayTarget.value = message
    } else {
      console.warn("[box-folder-picker]", message)
    }
  }
}
